import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  PhoneCall, PhoneIncoming, Sparkles, RefreshCw, CheckCircle2, XCircle,
  Voicemail,
} from "lucide-react";

function StatusPill({ s }) {
  const cls = {
    completed:       "bg-emerald-50 text-emerald-700 border-emerald-200",
    in_progress:     "bg-blue-50 text-blue-700 border-blue-200",
    voicemail_only:  "bg-amber-50 text-amber-700 border-amber-200",
  }[s] || "bg-neutral-100 text-neutral-500 border-neutral-200";
  return (
    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${cls}`}>
      {s?.replace(/_/g, " ") || "unknown"}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AiPhone() {
  const [cfg, setCfg] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ai-phone/settings");
      setCfg(data);
    } catch { /* silent */ }
    try {
      const { data } = await api.get("/ai-phone/sessions");
      setSessions(data || []);
    } catch { /* silent (403 for non-admin) */ }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (v) => {
    setBusy(true);
    try {
      await api.put("/ai-phone/settings", { ai_answer_enabled: v });
      setCfg((c) => ({ ...c, ai_answer_enabled: v }));
      toast.success(v ? "AI concierge live" : "AI concierge off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
            AI Phone Answering
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            When a seller calls your Twilio number, our AI concierge greets
            them, qualifies the deal (address, timeline, price, situation)
            and texts you a scored summary. Every call becomes a new lead.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="rounded-full h-10 px-3"
          data-testid="aip-refresh"
        >
          <RefreshCw size={13} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* Config card */}
      {cfg && (
        <div className="card-flat p-5" data-testid="aip-config-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <div className="font-display text-lg font-bold">Concierge</div>
                {cfg.ai_answer_enabled ? (
                  <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 border border-emerald-200">
                    <CheckCircle2 size={10} className="inline mr-1" /> Live
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest bg-neutral-100 text-neutral-500 rounded-full px-2 py-0.5">
                    Off
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Inbound number:{" "}
                {cfg.twilio_configured ? (
                  <span className="font-mono">{cfg.twilio_from_number}</span>
                ) : (
                  <span className="text-red-600">Twilio not configured</span>
                )}
                {" · "}
                Owner cell (SMS summary):{" "}
                {cfg.agent_phone ? (
                  <span className="font-mono">{cfg.agent_phone}</span>
                ) : (
                  <span className="text-amber-700">not set — set it under Communications → Call/SMS</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs">Turn on</span>
              <Switch
                checked={!!cfg.ai_answer_enabled}
                disabled={!cfg.is_admin || !cfg.twilio_configured || busy}
                onCheckedChange={toggle}
                data-testid="aip-toggle"
              />
            </div>
          </div>
          {!cfg.is_admin && (
            <div className="mt-3 text-xs text-amber-700">
              Only admins can toggle the AI concierge.
            </div>
          )}
          <div className="mt-4 text-xs text-muted-foreground border-t border-[var(--app-line)] pt-3">
            <Sparkles size={12} className="inline mr-1" />
            The concierge asks up to 6 questions (name, address, timeline,
            condition, price, motivation), scores motivation 0–100, creates
            a lead in stage <code className="mx-0.5 px-1 bg-neutral-100 rounded">new_lead</code>,
            and texts you the summary.
          </div>
        </div>
      )}

      {/* Sessions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-flat p-3 space-y-1" data-testid="aip-session-list">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 px-1">
            Recent Calls
          </div>
          {sessions.length === 0 ? (
            <div className="text-xs text-muted-foreground px-1 py-4">
              No AI-answered calls yet.
            </div>
          ) : (
            sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                data-testid={`aip-session-${s.id}`}
                className={`w-full text-left rounded-lg px-3 py-2 border transition-colors ${
                  selected?.id === s.id
                    ? "border-[var(--app-black)] bg-[var(--app-surface)]"
                    : "border-transparent hover:bg-[var(--app-surface)]"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium min-w-0">
                    {s.status === "voicemail_only"
                      ? <Voicemail size={12} className="text-amber-600 shrink-0" />
                      : <PhoneIncoming size={12} className="text-emerald-600 shrink-0" />}
                    <span className="truncate">
                      {(s.data?.name || "Caller").split(" ")[0]}
                    </span>
                  </div>
                  <StatusPill s={s.status} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                  <span className="font-mono truncate">{s.from_number}</span>
                  {typeof s.motivation === "number" && (
                    <span className="ml-auto">🔥 {s.motivation}</span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {fmt(s.started_at)}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <SessionDetail s={selected} />
          ) : (
            <div className="card-flat p-6 text-center text-sm text-muted-foreground">
              Select a call to see the transcript and captured slots.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SessionDetail({ s }) {
  const d = s.data || {};
  const rows = [
    ["Name",       d.name],
    ["Address",    d.address],
    ["Timeline",   d.timeline],
    ["Condition",  d.condition],
    ["Price",      d.price],
    ["Motivation", d.motivation],
  ];
  return (
    <div className="space-y-3">
      <div className="card-flat p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              {fmt(s.started_at)}{s.ended_at ? ` → ${fmt(s.ended_at)}` : ""}
            </div>
            <div className="font-display text-lg font-bold mt-1">
              {(d.name || "Caller")} · <span className="font-mono">{s.from_number}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusPill s={s.status} />
            {typeof s.motivation === "number" && (
              <div className="text-lg font-display font-bold">
                🔥 {s.motivation}/100
              </div>
            )}
            {s.lead_id && (
              <a href="/pipeline" className="text-[10px] uppercase tracking-widest text-[var(--app-accent)] hover:underline">
                View in Pipeline →
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-4">
          {rows.map(([k, v]) => (
            <div key={k} className="text-sm border-b border-[var(--app-line)] pb-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
              <div>{v || "—"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card-flat p-4">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
          Transcript
        </div>
        <div className="space-y-2 text-xs" data-testid="aip-transcript">
          {(s.transcript || []).map((row, i) => (
            <div key={i} className="border-b border-[var(--app-line)] last:border-b-0 pb-2">
              <div className="text-muted-foreground">{row.question}</div>
              <div className="mt-0.5">
                {row.answer ? (
                  <span className="text-[var(--app-text)]">↳ {row.answer}</span>
                ) : (
                  <span className="text-red-500">↳ (no speech)</span>
                )}
              </div>
            </div>
          ))}
          {(s.transcript || []).length === 0 && (
            <div className="text-muted-foreground">No transcript captured.</div>
          )}
        </div>
      </div>

      {s.voicemail_url && (
        <div className="card-flat p-4">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
            Voicemail (caller opted out of AI)
          </div>
          <audio controls src={`${s.voicemail_url}.mp3`} className="w-full" />
        </div>
      )}
    </div>
  );
}
